import { useState, useRef, useEffect } from "react";
import LocalStorage from "core/utils/localstorage";

const ls = LocalStorage("video-player");
const TIME_UPDATE_INTERVAL_MS = 100;
const BUFFERED_UPDATE_INTERVAL_MS = 500;
const AUTO_PLAY_DELAY_MS = 80;

export default function useMainVideoPlayer({
  initialLoading,
  onActiveVideoReady,
}) {
  const mainVideoRef = useRef(null);
  const mainVideoContainerRef = useRef(null);
  const [isMainVideoMounted, setIsMainVideoMounted] = useState(false);

  // Ref для отслеживания пользовательских действий
  const userPausedRef = useRef(false);

  // Состояние активного видео для контролов
  const [activeVideoData, setActiveVideoData] = useState(null);
  const [videoState, setVideoState] = useState({
    status: "loading",
    duration: 0,
    currentTime: 0,
    buffered: [],
    isUserPause: false,
    volume: ls.getItem("volume") ?? 1,
    muted: ls.getItem("muted") ?? true,
    isLoading: false,
    hlsReady: false,
  });

  // Действия для управления главным видео
  const videoActions = {
    play: () => {
      if (mainVideoRef.current) {
        userPausedRef.current = false; // Сбрасываем флаг пользовательской паузы
        mainVideoRef.current.play().catch(console.error);
      }
    },
    pause: () => {
      if (mainVideoRef.current) {
        userPausedRef.current = true; // Устанавливаем флаг пользовательской паузы
        mainVideoRef.current.pause();
      }
    },
    seek: (time) => {
      if (mainVideoRef.current) {
        mainVideoRef.current.currentTime = time;
      }
    },
    navigate: (time) => {
      // Алиас для seek - для совместимости с ShortySeekBar
      if (mainVideoRef.current) {
        mainVideoRef.current.currentTime = time;
      }
    },
    setVolume: (volume) => {
      if (mainVideoRef.current) {
        mainVideoRef.current.volume = volume;
      }
    },
    setMuted: (muted) => {
      if (mainVideoRef.current) {
        mainVideoRef.current.muted = muted;
      }
    },
    // 🔴 Исправление: добавляем toggleMute для ShortyControlPanel
    toggleMute: () => {
      if (mainVideoRef.current) {
        mainVideoRef.current.muted = !mainVideoRef.current.muted;
      }
    },
  };

  // Callback для получения данных активного видео
  const handleActiveVideoReady = (videoData) => {
    let isNewVideo = false;

    // Обновляем только если данные действительно изменились
    setActiveVideoData((prev) => {
      if (
        prev?.content?.id === videoData?.content?.id &&
        prev?.playbackUrl === videoData?.playbackUrl
      ) {
        return prev;
      }
      isNewVideo = true; // Помечаем что это новое видео
      return videoData;
    });

    // Вызываем внешний callback если передан
    if (onActiveVideoReady) {
      onActiveVideoReady(videoData);
    }

    // Автозапуск только для нового видео
    if (isNewVideo) {
      setTimeout(() => {
        if (mainVideoRef.current && !userPausedRef.current) {
          // Проверяем состояние видео элемента
          const isAlreadyPlaying = !mainVideoRef.current.paused;
          const isReadyToPlay = mainVideoRef.current.readyState >= 2; // HAVE_CURRENT_DATA

          if (isAlreadyPlaying) {
            return;
          }

          if (!isReadyToPlay) {
            return;
          }

          mainVideoRef.current.play().catch(() => {});
        }
      }, AUTO_PLAY_DELAY_MS);
    }
  };

  // Создание главного video элемента
  useEffect(() => {
    if (initialLoading) {
      return;
    }

    if (typeof document === "undefined" || !mainVideoContainerRef.current) {
      return;
    }

    // Если video уже создан, не создаваем повторно
    if (mainVideoRef.current) {
      return;
    }

    const video = document.createElement("video");
    video.muted = ls.getItem("muted") ?? true;
    video.volume = ls.getItem("volume") ?? 1;
    video.playsInline = true;
    video.style.width = "100%";
    video.style.height = "100%";
    video.style.objectFit = "cover";
    video.style.zIndex = "0"; // Видео под контролами
    video.style.opacity = "1";
    video.style.transition = "opacity 80ms ease-out";
    video.autoplay = true;
    video.preload = "auto";
    video.id = "main-video";
    video.loop = false;

    // Event handlers с throttling для timeupdate
    const handleLoadedMetadata = () => {
      setVideoState((prev) => ({
        ...prev,
        duration: video.duration,
        status: "ready",
      }));
    };

    const handleTimeUpdate = () => {
      const now = Date.now();
      if (
        video._lastTimeUpdate &&
        now - video._lastTimeUpdate < TIME_UPDATE_INTERVAL_MS
      ) {
        return;
      }
      video._lastTimeUpdate = now;

      let buffered;
      if (
        !video._lastBufferedUpdate ||
        now - video._lastBufferedUpdate >= BUFFERED_UPDATE_INTERVAL_MS
      ) {
        buffered = [];
        for (let i = 0; i < video.buffered.length; i++) {
          buffered.push({
            start: video.buffered.start(i),
            end: video.buffered.end(i),
          });
        }
        video._lastBufferedUpdate = now;
      }

      setVideoState((prev) => {
        // Если currentTime растет и видео не на паузе, значит оно играет
        const isActuallyPlaying = !video.paused && video.currentTime > 0;
        const shouldBePlayingStatus =
          isActuallyPlaying && prev.status !== "playing";

        return {
          ...prev,
          currentTime: video.currentTime,
          buffered: buffered || prev.buffered,
          // Обновляем статус если видео фактически играет, но статус не "playing"
          status: shouldBePlayingStatus ? "playing" : prev.status,
        };
      });
    };

    const handlePlay = () => {
      setVideoState((prev) => ({
        ...prev,
        status: "playing",
        isLoading: false,
      }));
    };

    const handlePause = () => {
      setVideoState((prev) => ({ ...prev, status: "paused" }));
    };

    const handleEnded = () => {
      setVideoState((prev) => ({ ...prev, status: "ended" }));
    };

    const handleVolumeChange = () => {
      ls.setItem("volume", video.volume);
      ls.setItem("muted", video.muted);
      setVideoState((prev) => ({
        ...prev,
        volume: video.volume,
        muted: video.muted,
      }));
    };

    const handleWaiting = () => {
      setVideoState((prev) => ({ ...prev, isLoading: true }));
    };

    const handleCanPlay = () => {
      setVideoState((prev) => ({ ...prev, isLoading: false }));
    };

    // Добавляем event listeners
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("volumechange", handleVolumeChange);
    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("canplay", handleCanPlay);

    // Сохраняем handlers для cleanup
    video._eventHandlers = {
      handleLoadedMetadata,
      handleTimeUpdate,
      handlePlay,
      handlePause,
      handleEnded,
      handleVolumeChange,
      handleWaiting,
      handleCanPlay,
    };

    // Устанавливаем ref и добавляем в DOM
    mainVideoRef.current = video;
    mainVideoContainerRef.current.appendChild(video);
    setIsMainVideoMounted(true);

    return () => {
      if (mainVideoRef.current) {
        const video = mainVideoRef.current;
        const handlers = video._eventHandlers;

        // Явно останавливаем видео ПЕРЕД очисткой (критично для iOS)
        if (!video.paused) {
          video.pause();
        }

        // Сбрасываем src для полной остановки загрузки
        video.src = "";
        video.load();

        if (handlers) {
          video.removeEventListener(
            "loadedmetadata",
            handlers.handleLoadedMetadata
          );
          video.removeEventListener("timeupdate", handlers.handleTimeUpdate);
          video.removeEventListener("play", handlers.handlePlay);
          video.removeEventListener("pause", handlers.handlePause);
          video.removeEventListener("ended", handlers.handleEnded);
          video.removeEventListener(
            "volumechange",
            handlers.handleVolumeChange
          );
          video.removeEventListener("waiting", handlers.handleWaiting);
          video.removeEventListener("canplay", handlers.handleCanPlay);
        }

        if (video.parentNode) {
          video.remove();
        }
        mainVideoRef.current = null;
        setIsMainVideoMounted(false);
      }
    };
  }, [initialLoading]); // Выполняется только один раз при монтировании

  return {
    mainVideoRef,
    mainVideoContainerRef,
    isMainVideoMounted,
    userPausedRef,
    activeVideoData,
    videoState,
    videoActions,
    handleActiveVideoReady,
  };
}
